import { JSX, useEffect, useState } from 'react';
import MainContent from './MainContent.tsx';
import Header from './Header.tsx';
import Sidebar from './SideBar.tsx';
import { useSearchParams } from 'react-router-dom';
import SnackbarComponent from '../../common/components/SnackbarComponent.tsx';
import { useGlobalStore } from '../store/store.tsx';
import { useLocalStorage } from '../../features/auth/hooks/useLocalStorage.tsx';
import { useTheme, useMediaQuery, Box } from '@mui/material';

interface LayoutComponentProps {
    child: React.ReactNode;
}

const LayoutComponent = ({ child }: LayoutComponentProps): JSX.Element => {
    const [searchParams] = useSearchParams();
    const [isEmbedded, setIsEmbedded] = useLocalStorage('isEmbedded', null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'), {
        noSsr: true,
    });
    const isTablet = useMediaQuery(theme.breakpoints.between(768, 1024));
    const isSmallScreen = isMobile || isTablet;

    // Start with sidebar open on desktop, closed on mobile/tablet
    const [isSidebarOpen, setIsSidebarOpen] = useState(!isSmallScreen);

    useEffect(() => {
        // Update sidebar state when screen size changes
        setIsSidebarOpen(!isSmallScreen);
    }, [isSmallScreen]);

    useEffect(() => {
        if (searchParams.get('embedded') === 'true' && isEmbedded !== 'true') {
            setIsEmbedded('true');
        }
        if (
            searchParams.get('embedded') === 'false' &&
            isEmbedded !== 'false'
        ) {
            useLocalStorage('isEmbedded', null);
            setIsEmbedded('false');
        }
    }, [isEmbedded, searchParams, setIsEmbedded]);

    const { message, messageType, isMessageVisible, clearMessage } =
        useGlobalStore();

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    if (isEmbedded == 'true') {
        return (
            <div className="flex flex-col min-h-screen">
                <SnackbarComponent
                    open={isMessageVisible}
                    message={message}
                    severity={messageType}
                    onClose={clearMessage}
                />
                <MainContent child={child} />
            </div>
        );
    }

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', overflow: 'hidden' }}>
            <SnackbarComponent
                open={isMessageVisible}
                message={message}
                severity={messageType}
                onClose={clearMessage}
            />

            <Header
                toggleSidebar={toggleSidebar}
                isSmallScreen={isSmallScreen}
            />

            <Sidebar
                isOpen={isSidebarOpen}
                toggleSidebar={toggleSidebar}
                isMobile={isSmallScreen}
            />

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    overflowY: 'auto',
                    bgcolor: 'grey.50',
                    px: { xs: 2, sm: 3 },
                    mt: '64px',
                }}
            >
                {child}
            </Box>
        </Box>
    );
};

export default LayoutComponent;
